import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { Download, Printer, Loader } from "lucide-react";

export default function BingoCertificate() {
  const shopName = useSelector((s) => s.auth.employeeDetails?.others?.merchant_shop_name);
  const mobile   = useSelector((s) => s.auth.employeeDetails?.mobile_number);

  const [pdfUrl, setPdfUrl]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const blobRef = useRef(null);

  useEffect(() => {
    generatePdf();
    return () => {
      if (blobRef.current) URL.revokeObjectURL(blobRef.current);
    };
  }, [shopName, mobile]);

  const generatePdf = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/documents/BINGOPAY Certificate-2.pdf");
      if (!res.ok) throw new Error("Failed to load certificate file.");
      const arrayBuffer = await res.arrayBuffer();

      // Load original PDF
      const origDoc = await PDFDocument.load(arrayBuffer);
      const { width, height } = origDoc.getPages()[0].getSize();

      // Create a CLEAN overlay doc — fresh coordinate space, no inherited CTM
      const overlayDoc = await PDFDocument.create();
      const overlayPage = overlayDoc.addPage([width, height]);
      const font = await overlayDoc.embedFont(StandardFonts.HelveticaBold);

      // Draw original certificate as background (so our text sits on top)
      const [origEmbed] = await overlayDoc.embedPdf(origDoc, [0]);
      overlayPage.drawPage(origEmbed);

      // Page: 841.89 × 595.28 pt (A4 landscape)
      // Underline blank spans x≈300–620 pt for both fields (center ≈ 460 pt)
      // y calibrated via ruler overlay: M/S baseline=height-154, Mobile=height-241
      const FONT_SIZE = 24;
      const MS_CENTER_X   = 460;
      const MOB_CENTER_X  = 462;
      const LINE_X_MIN    = 300; // don't let text start before the underline

      if (shopName) {
        const shopText  = String(shopName);
        const shopWidth = font.widthOfTextAtSize(shopText, FONT_SIZE);
        const shopX     = Math.max(LINE_X_MIN, MS_CENTER_X - shopWidth / 2);
        overlayPage.drawText(shopText, {
          x:    shopX,
          y:    height - 154,
          size: FONT_SIZE,
          font,
          color: rgb(0.05, 0.05, 0.05),
        });
      }

      if (mobile) {
        const mobText  = String(mobile);
        const mobWidth = font.widthOfTextAtSize(mobText, FONT_SIZE);
        const mobX     = Math.max(LINE_X_MIN, MOB_CENTER_X - mobWidth / 2);
        overlayPage.drawText(mobText, {
          x:    mobX,
          y:    height - 241,
          size: FONT_SIZE,
          font,
          color: rgb(0.05, 0.05, 0.05),
        });
      }

      const pdfBytes = await overlayDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      if (blobRef.current) URL.revokeObjectURL(blobRef.current);
      const url = URL.createObjectURL(blob);
      blobRef.current = url;
      setPdfUrl(url);
    } catch (e) {
      setError(e.message || "Failed to generate certificate.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!pdfUrl) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = "BingoPay_Certificate.pdf";
    a.click();
  };

  const handlePrint = () => {
    if (!pdfUrl) return;
    const win = window.open(pdfUrl, "_blank");
    win?.print();
  };

  return (
    <div style={{ backgroundColor: "#ffffff", padding: "16px", overflowX: "hidden" }}>

      {/* Action bar */}
      <div style={{ marginBottom: "12px" }}>
        <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
          <button
            onClick={handleDownload}
            disabled={!pdfUrl}
            style={{
              display: "flex", alignItems: "center", gap: "8px",
              padding: "8px 16px", background: "#fff",
              border: "1.5px solid #cbd5e1", color: "#334155",
              fontSize: "13px", fontWeight: 600, borderRadius: "10px",
              cursor: pdfUrl ? "pointer" : "not-allowed", opacity: pdfUrl ? 1 : 0.5,
            }}
          >
            <Download size={15} /> Download PDF
          </button>
          <button
            onClick={handlePrint}
            disabled={!pdfUrl}
            style={{
              display: "flex", alignItems: "center", gap: "8px",
              padding: "8px 16px", background: "#12345A",
              border: "none", color: "#fff",
              fontSize: "13px", fontWeight: 600, borderRadius: "10px",
              cursor: pdfUrl ? "pointer" : "not-allowed", opacity: pdfUrl ? 1 : 0.5,
            }}
          >
            <Printer size={15} /> Print Certificate
          </button>
        </div>
      </div>

      {loading && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "96px 0", gap: "12px" }}>
          <Loader size={32} color="#12345A" style={{ animation: "spin 1s linear infinite" }} />
          <p style={{ color: "#64748b", fontSize: "14px" }}>Generating certificate…</p>
        </div>
      )}

      {error && (
        <div style={{ padding: "12px 16px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "10px", color: "#b91c1c", fontSize: "14px", marginBottom: "12px" }}>
          {error}
        </div>
      )}

      {pdfUrl && !loading && (
        <iframe
          src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitPage`}
          title="BingoPay Certificate"
          style={{
            display: "block",
            width: "100%",
            height: "calc(100vh - 200px)",
            border: "none",
            outline: "none",
            background: "transparent",
          }}
        />
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
